import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthSessionService } from '../services/auth-session.service';
import { Router } from '@angular/router';
import { inject } from '@angular/core';
import { LanguageService } from '../services/language.service';
import { FORCE_USER_TOKEN } from '../http-context/force-user-token.context';

const skipUrls = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/admin-auth/login',
  '/assets/i18n/',
];

const addToken = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const handle401Error = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthSessionService,
): Observable<HttpEvent<unknown>> => {
  if (!authService.isRefreshing) {
    authService.isRefreshing = true;
    authService.refreshSubject.next(null);

    return authService.refresh().pipe(
      switchMap((response) => {
        authService.isRefreshing = false;
        authService.refreshSubject.next(response.accessToken);
        return next(addToken(req, response.accessToken));
      }),
      catchError((error) => {
        authService.resetRefreshOnFailure(error);
        authService.logout().subscribe();
        return throwError(() => error);
      }),
    );
  }

  return authService.refreshSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap((token) => next(addToken(req, token!))),
    catchError((err) => {
      // If the refresh failed with a 401, convert to a plain Error so the outer
      // catchError does not re-enter handle401Error, preventing infinite loops.
      const is401 = err instanceof HttpErrorResponse && err.status === 401;
      return throwError(() => (is401 ? new Error('Token refresh failed') : err));
    }),
  );
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthSessionService);
  const router = inject(Router);
  const languageService = inject(LanguageService);

  if (skipUrls.some((url) => req.url.includes(url))) {
    return next(req);
  }

  const userToken = authService.getAccessToken();
  const adminToken = localStorage.getItem('admin_access_token');

  // When navigating on admin routes, always use the admin token — even if a
  // user token exists in memory (e.g. left over from a principal impersonation).
  // Using a user token (role:'user') on admin endpoints causes 403s from RolesGuard.
  // FORCE_USER_TOKEN overrides this for calls that are always about the
  // school-user session regardless of the current page (see its own doc).
  const isAdminRoute = router.url.startsWith('/admin') && !req.context.get(FORCE_USER_TOKEN);
  const token = isAdminRoute && adminToken ? adminToken : (userToken ?? adminToken);
  const isAdminRequest = (isAdminRoute && !!adminToken) || (!userToken && !!adminToken);

  // Tell the API when a request is made from the teacher shell so it can
  // scope a principal-who-also-teaches down to their own allocations even
  // though their account still holds the principal role (see the sidebar's
  // "Switch to Teacher" role toggle). Only ever narrows access server-side —
  // never granted, so a plain teacher sending this is a no-op and a
  // principal without the teacher role just sees an empty teacher view.
  const isTeacherRoute = router.url.startsWith('/teacher');

  // Forward the active language as Accept-Language so the API returns
  // translated error messages in the user's preferred language.
  const langReq = req.clone({
    setHeaders: {
      'Accept-Language': languageService.getAcceptLanguageHeader(),
      ...(isTeacherRoute ? { 'X-Active-Role': 'teacher' } : {}),
    },
  });
  const authReq = token ? addToken(langReq, token) : langReq;

  return next(authReq).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        if (isAdminRequest) {
          localStorage.removeItem('admin_access_token');
          router.navigate(['/admin/login']);
          return throwError(() => error);
        }
        return handle401Error(authReq, next, authService);
      }
      return throwError(() => error);
    }),
  );
};
